import React, { useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { BaseApiServiceImpl } from "../../app_utils/api/BaseApiServiceImpl";

interface GatewayHealthCheck {
  id: number;
  gatewayType: string;
  gatewayId: number;
  gatewayName: string;
  providerName: string;
  upstreamBalance: number | null;
  currency: string | null;
  sentToday: number | null;
  dailyLimit: number | null;
  creditsPerUser: number | null;
  healthStatus: string;
  lastCheckedAt: string;
  lastError: string | null;
}

interface Props {
  healthData: GatewayHealthCheck[];
  loading: boolean;
}

const statusConfig: Record<string, { color: string; bg: string; icon: string; label: string }> = {
  HEALTHY: { color: "#16a34a", bg: "#dcfce7", icon: PrimeIcons.CHECK_CIRCLE, label: "Healthy" },
  LOW_BALANCE: { color: "#d97706", bg: "#fef3c7", icon: PrimeIcons.EXCLAMATION_TRIANGLE, label: "Low Balance" },
  CRITICAL: { color: "#dc2626", bg: "#fee2e2", icon: PrimeIcons.EXCLAMATION_TRIANGLE, label: "Critical" },
  UNREACHABLE: { color: "#6b7280", bg: "#f3f4f6", icon: PrimeIcons.BAN, label: "Unreachable" },
  UNKNOWN: { color: "#6b7280", bg: "#f3f4f6", icon: PrimeIcons.QUESTION, label: "Unknown" },
};

const typeIcons: Record<string, string> = {
  SMS: PrimeIcons.COMMENT,
  EMAIL: PrimeIcons.ENVELOPE,
  PAYMENT: PrimeIcons.DOLLAR,
};

const GatewayHealthCards = ({ healthData, loading }: Props) => {
  const [selectedGateway, setSelectedGateway] = useState<GatewayHealthCheck | null>(null);
  const [historyData, setHistoryData] = useState<any[]>([]);

  const formatBalance = (balance: number | null, currency: string | null, gatewayType?: string) => {
    if (balance === null || balance === undefined) return "N/A";
    if (currency) return `${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    if (gatewayType === "SMS" || gatewayType === "EMAIL") return `${balance.toLocaleString()} credits`;
    return balance.toLocaleString();
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return "Never";
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const fetchHistory = async (gateway: GatewayHealthCheck) => {
    setSelectedGateway(gateway);
    try {
      const response = await new BaseApiServiceImpl(
        `/api/v1/gateway-health/${gateway.gatewayType}/${gateway.gatewayId}/history?days=7`
      ).getRequestWithJsonResponse({});
      setHistoryData(response?.data || []);
    } catch {
      setHistoryData([]);
    }
  };

  if (loading) {
    return (
      <div className="grid mb-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="col-12 md:col-4">
            <div className="card" style={{ minHeight: "160px" }}>
              <div className="text-500">Loading...</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!healthData || healthData.length === 0) {
    return (
      <div className="card mb-3">
        <div className="flex align-items-center text-500">
          <i className={PrimeIcons.INFO_CIRCLE} style={{ marginRight: "0.5rem" }} />
          <span>N/A</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid mb-3">
        {healthData.map((gw) => {
          const config = statusConfig[gw.healthStatus] || statusConfig.UNKNOWN;
          const typeIcon = typeIcons[gw.gatewayType] || PrimeIcons.COMPASS;
          return (
            <div key={`${gw.gatewayType}-${gw.gatewayId}`} className="col-12 md:col-4">
              <div
                className="card cursor-pointer"
                style={{ borderTop: `3px solid ${config.color}`, minHeight: "160px" }}
                onClick={() => fetchHistory(gw)}
              >
                <div className="flex align-items-center justify-content-between mb-3">
                  <div className="flex align-items-center">
                    <i className={typeIcon} style={{ fontSize: "1.25rem", color: config.color, marginRight: "0.5rem" }} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>{gw.gatewayName}</div>
                      <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{gw.providerName}</div>
                    </div>
                  </div>
                  <span
                    style={{
                      background: config.bg,
                      color: config.color,
                      borderRadius: "9999px",
                      padding: "0.15rem 0.6rem",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {config.label}
                  </span>
                </div>

                <div className="grid" style={{ fontSize: "0.85rem" }}>
                  <div className="col-6">
                    <div style={{ color: "#6b7280" }}>Available Balance</div>
                    <div style={{ fontWeight: 600, fontSize: "1rem" }}>
                      {formatBalance(gw.upstreamBalance, gw.currency, gw.gatewayType)}
                    </div>
                  </div>
                  <div className="col-6">
                    <div style={{ color: "#6b7280" }}>Per User</div>
                    <div style={{ fontWeight: 600 }}>
                      {gw.creditsPerUser !== null
                        ? gw.creditsPerUser.toFixed(1)
                        : "N/A"}
                    </div>
                  </div>
                  {gw.sentToday !== null && gw.dailyLimit !== null && (
                    <>
                      <div className="col-6">
                        <div style={{ color: "#6b7280" }}>Sent Today</div>
                        <div style={{ fontWeight: 600 }}>{gw.sentToday}</div>
                      </div>
                      <div className="col-6">
                        <div style={{ color: "#6b7280" }}>Daily Limit</div>
                        <div style={{ fontWeight: 600 }}>{gw.dailyLimit?.toLocaleString()}</div>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "0.75rem" }}>
                  Last checked: {formatTime(gw.lastCheckedAt)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog
        header={selectedGateway ? `${selectedGateway.gatewayName} - History` : "History"}
        visible={!!selectedGateway}
        style={{ width: "50vw" }}
        onHide={() => {
          setSelectedGateway(null);
          setHistoryData([]);
        }}
      >
        {historyData.length === 0 ? (
          <div className="text-500 p-3">No history data available yet.</div>
        ) : (
          <div>
            <table style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "0.5rem" }}>Time</th>
                  <th style={{ textAlign: "right", padding: "0.5rem" }}>Balance</th>
                  <th style={{ textAlign: "right", padding: "0.5rem" }}>Per User</th>
                  <th style={{ textAlign: "center", padding: "0.5rem" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {historyData.map((h: any, idx: number) => {
                  const config = statusConfig[h.healthStatus] || statusConfig.UNKNOWN;
                  return (
                    <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "0.5rem" }}>{formatTime(h.lastCheckedAt)}</td>
                      <td style={{ textAlign: "right", padding: "0.5rem" }}>
                        {formatBalance(h.upstreamBalance, h.currency, h.gatewayType)}
                      </td>
                      <td style={{ textAlign: "right", padding: "0.5rem" }}>
                        {h.creditsPerUser !== null ? h.creditsPerUser.toFixed(1) : "N/A"}
                      </td>
                      <td style={{ textAlign: "center", padding: "0.5rem" }}>
                        <span
                          style={{
                            background: config.bg,
                            color: config.color,
                            borderRadius: "9999px",
                            padding: "0.1rem 0.5rem",
                            fontSize: "0.75rem",
                          }}
                        >
                          {config.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default GatewayHealthCards;
